import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Building2,
  ChevronRight,
  Clock,
  Edit,
  KeyRound,
  PhoneCall,
  Plus,
  ShieldAlert,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState, PageHeader, StatusPill } from "@/components/uiext";
import { InlineEditorShell } from "@/components/InlineEditorShell";
import { Switch } from "@/components/ui/switch";
import { useConfirm } from "@/lib/confirm.jsx";
import { toast } from "sonner";

// Phase 6o Hotfix G2 — shared reltime helper for the "provisioned
// 3d ago" sub-line. Intentionally tiny and locale-agnostic.
function relTimeShort(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  const now = Date.now();
  if (Number.isNaN(d.getTime())) return null;
  const diff = Math.max(0, (now - d.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.round(diff / 86400)}d ago`;
  if (diff < 31536000) return `${Math.round(diff / 2592000)}mo ago`;
  return `${Math.round(diff / 31536000)}y ago`;
}

// Phase 6o Hotfix G2 — LED semantics for platform tenants. Matches the
// 3CX/Vodia-style 10×10 rounded square used on Trunks for single-glance
// telegraphy. Semantics:
//   - active    → green, pulsing  (healthy, operational)
//   - pending   → cyan (info)     (needs your attention / approval)
//   - suspended → red              (operator-halted)
//   - anything  → grey             (unknown / migrated states)
// Intentionally distinct from amber (reserved for "probing / in motion"
// on Trunks) so an operator never confuses "tenant awaiting approval"
// with "trunk probing the carrier".
function tenantLedState(status) {
  switch (status) {
    case "active":
      return { cls: "bg-[hsl(var(--ok))] animate-pulse", tone: "ok" };
    case "pending":
      return { cls: "bg-[hsl(var(--info))]", tone: "info" };
    case "suspended":
      return { cls: "bg-[hsl(var(--danger))]", tone: "danger" };
    default:
      return { cls: "bg-muted-foreground/40", tone: "muted" };
  }
}

function TenantLED({ status, title }) {
  const s = tenantLedState(status);
  return (
    <span
      className={`h-2.5 w-2.5 rounded-[2px] shrink-0 ${s.cls}`}
      aria-label={`tenant-led-${status || "unknown"}`}
      title={title}
      data-testid="tenant-led"
    />
  );
}

const EMPTY = {
  name: "",
  slug: "",
  plan: "starter",
  status: "active",
  limits: { channels: 10, max_trunks: 3, max_domains: 5, max_customers: 20 },
  features: { api: false, recording: false, failover: false, csv_export: true, cdr_retention_days: 30 },
  owner_email: "",
  owner_password: "",
  // Phase 6r — billing/trial state. New tenant defaults: not exempt,
  // no manual trial date (engine will auto-stamp at create-time when
  // the chosen plan is "free").
  trial_expires_at: null,
  billing_exempt: false,
  billing_exempt_until: null,
  billing_notes: "",
};

// Phase 6r — convert ISO datetime <-> YYYY-MM-DD for HTML date inputs.
// We persist full ISO timestamps (so the engine can compare to UTC now)
// but the editor surface only collects a date.
function isoToDateInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    // Use UTC components to avoid the day-shift you get from local-tz parsing.
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(d.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  } catch {
    return "";
  }
}

// Phase 6r — visual semantics for the billing-state badge.
// state -> { label, tone, blocked }
const BILLING_STATE_META = {
  paid:           { label: "Paid",          tone: "bg-[hsl(var(--ok))]/15 text-[hsl(var(--ok))] border-[hsl(var(--ok))]/40" },
  trial_active:   { label: "Trial",         tone: "bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/40" },
  trial_expired:  { label: "Expired",       tone: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/40" },
  exempt:         { label: "Exempt",        tone: "bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] border-[hsl(var(--accent))]/40" },
  unlimited:      { label: "Unlimited",     tone: "bg-muted/40 text-muted-foreground border-border" },
  unknown:        { label: "—",             tone: "bg-muted/30 text-muted-foreground border-border" },
};

function BillingBadge({ status }) {
  if (!status) return null;
  const meta = BILLING_STATE_META[status.state] || BILLING_STATE_META.unknown;
  const daysLeft = status.days_left;
  let label = meta.label;
  if (status.state === "trial_active" && typeof daysLeft === "number") {
    label = `Trial · ${daysLeft}d`;
  }
  return (
    <span
      className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${meta.tone}`}
      title={status.reason || meta.label}
      data-testid={`billing-badge-${status.state}`}
    >
      {label}
    </span>
  );
}

function ManageUsersDialog({ org, open, onOpenChange }) {
  const confirmFn = useConfirm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  // Password reset sub-dialog
  const [pwTarget, setPwTarget] = useState(null); // a user row
  const [pwValue, setPwValue] = useState("");
  const [pwForce, setPwForce] = useState(true);

  const load = async () => {
    if (!org?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/platform/orgs/${org.id}/users`);
      setUsers(r.data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setPwTarget(null);
      setPwValue("");
      setNewEmail("");
      setNewPassword("");
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, org?.id]);

  const createUser = async () => {
    if (!newEmail.includes("@") || newPassword.length < 6) {
      toast.error("Email + password (≥6 chars) required");
      return;
    }
    setCreating(true);
    try {
      await api.post(`/platform/orgs/${org.id}/users`, {
        email: newEmail.trim().toLowerCase(),
        password: newPassword,
        must_change_password: true,
        role: "owner",
      });
      toast.success("User created");
      setNewEmail("");
      setNewPassword("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const toggleEnabled = async (u) => {
    try {
      await api.patch(`/platform/orgs/${org.id}/users/${u.id}`, {
        enabled: !u.enabled,
      });
      toast.success(`${u.email} ${!u.enabled ? "enabled" : "disabled"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const deleteUser = async (u) => {
    const ok = await confirmFn({
      title: `Remove ${u.email} from ${org.name}?`,
      description: "They will lose access immediately. This cannot be undone.",
      danger: true,
      confirmLabel: "Remove",
    });
    if (!ok) return;
    try {
      await api.delete(`/platform/orgs/${org.id}/users/${u.id}`);
      toast.success("User removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const submitReset = async () => {
    if (pwValue.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await api.post(
        `/platform/orgs/${org.id}/users/${pwTarget.id}/reset-password`,
        {
          new_password: pwValue,
          must_change_password: pwForce,
        },
      );
      toast.success(`Password reset for ${pwTarget.email}`);
      setPwTarget(null);
      setPwValue("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const randomPassword = () => {
    // 16 hex chars — easy to read over the phone when resetting someone's login
    const buf = new Uint8Array(8);
    window.crypto.getRandomValues(buf);
    const pw = Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
    setPwValue(pw);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="manage-users-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-4 w-4" /> Users — {org?.name}
          </DialogTitle>
          <p className="text-xs text-muted-foreground pt-0.5">
            Manage the web-login users for this tenant. Use{" "}
            <span className="font-medium">Reset password</span> when a customer
            has been locked out.
          </p>
        </DialogHeader>

        {/* Create new user */}
        <div className="rounded-md border border-border p-3 space-y-2 bg-muted/30">
          <div className="flex items-center gap-2 text-xs font-medium">
            <UserPlus className="h-3.5 w-3.5" /> Add user
          </div>
          <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
            <div>
              <Label className="text-xs">Email</Label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="person@company.com"
                data-testid="new-user-email"
              />
            </div>
            <div>
              <Label className="text-xs">Temp password</Label>
              <Input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="min 6 chars"
                data-testid="new-user-password"
              />
            </div>
            <Button
              onClick={createUser}
              disabled={creating}
              data-testid="new-user-create"
            >
              {creating ? "Adding…" : "Add"}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            The new user will be forced to change this password on first login.
          </p>
        </div>

        {/* Existing users */}
        <div className="border border-border rounded-md overflow-hidden">
          {loading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              Loading…
            </div>
          ) : users.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No users in this tenant yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`org-user-row-${u.id}`}>
                    <TableCell>
                      <div className="font-medium">{u.email}</div>
                      {u.must_change_password && (
                        <div className="text-[11px] text-[hsl(var(--security))]">
                          must change password
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground">
                      {u.role || "owner"}
                    </TableCell>
                    <TableCell>
                      <StatusPill kind={u.enabled ? "ok" : "danger"}>
                        {u.enabled ? "enabled" : "disabled"}
                      </StatusPill>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap space-x-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setPwTarget(u);
                          setPwValue("");
                          setPwForce(true);
                        }}
                        data-testid={`reset-pw-${u.id}`}
                      >
                        <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset password
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => toggleEnabled(u)}
                        title={u.enabled ? "Disable" : "Enable"}
                        data-testid={`toggle-enabled-${u.id}`}
                      >
                        <Switch checked={u.enabled !== false} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-[hsl(var(--danger))]"
                        onClick={() => deleteUser(u)}
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>

        {/* Password reset sub-dialog */}
        <Dialog open={!!pwTarget} onOpenChange={(v) => !v && setPwTarget(null)}>
          <DialogContent className="max-w-md" data-testid="reset-pw-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound className="h-4 w-4" /> Reset password
              </DialogTitle>
              <p className="text-xs text-muted-foreground pt-0.5">
                Setting a new password for{" "}
                <span className="font-mono font-medium">{pwTarget?.email}</span>.
                Share it over a secure channel.
              </p>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>New password</Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={pwValue}
                    onChange={(e) => setPwValue(e.target.value)}
                    placeholder="min 6 chars"
                    data-testid="reset-pw-value"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={randomPassword}
                    title="Generate random"
                    data-testid="reset-pw-random"
                  >
                    Generate
                  </Button>
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                <Switch
                  checked={pwForce}
                  onCheckedChange={(v) => setPwForce(!!v)}
                  data-testid="reset-pw-force"
                />
                <span>
                  Force the user to change this on their next login (recommended)
                </span>
              </label>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setPwTarget(null)}>
                Cancel
              </Button>
              <Button onClick={submitReset} data-testid="reset-pw-submit">
                Set new password
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

export default function TenantsAdmin() {
  const { t } = useTranslation();
  const confirmFn = useConfirm();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  // Users management side-dialog
  const [usersDialogOrg, setUsersDialogOrg] = useState(null);

  const load = () => {
    const qs = status ? `?status_filter=${status}` : "";
    api.get(`/platform/orgs${qs}`).then((r) => setItems(r.data));
  };
  useEffect(() => {
    load();
    const tick = setInterval(load, 10000);
    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const save = async () => {
    try {
      const body = {
        ...form,
        limits: {
          channels: Number(form.limits.channels || 0),
          max_trunks: Number(form.limits.max_trunks || 0),
          max_domains: Number(form.limits.max_domains || 0),
          max_customers: Number(form.limits.max_customers || 0),
        },
      };
      if (editing) {
        const { owner_email, owner_password, ...rest } = body;
        await api.patch(`/platform/orgs/${editing.id}`, rest);
        toast.success("Tenant updated");
      } else {
        await api.post("/platform/orgs", body);
        toast.success("Tenant created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const approve = async (o) => {
    await api.patch(`/platform/orgs/${o.id}`, { status: "active" });
    toast.success(`Approved ${o.name}`);
    load();
  };
  const suspend = async (o) => {
    const ok = await confirmFn({
      title: `Suspend "${o.name}"?`,
      description: "Their SIP traffic will be rejected immediately, but data is preserved. Platform admins are emailed.",
      danger: true,
      confirmLabel: "Suspend",
    });
    if (!ok) return;
    // Phase H — use the convenience endpoint so the action emits a
    // security alert + audit row. Falls through to PATCH on older backends.
    try {
      await api.post(`/platform/orgs/${o.id}/suspend`, { reason: "manual" });
    } catch {
      await api.patch(`/platform/orgs/${o.id}`, { status: "suspended" });
    }
    toast.success(`Suspended ${o.name}`);
    load();
  };
  const reactivate = async (o) => {
    try {
      await api.post(`/platform/orgs/${o.id}/resume`);
    } catch {
      await api.patch(`/platform/orgs/${o.id}`, { status: "active" });
    }
    toast.success(`Reactivated ${o.name}`);
    load();
  };
  const del = async (o) => {
    const ok = await confirmFn({
      title: `HARD DELETE "${o.name}"?`,
      description:
        "This permanently removes the tenant and ALL its data (users, trunks, domains, CDRs, registrations). This cannot be undone.",
      danger: true,
      confirmLabel: "Delete forever",
    });
    if (!ok) return;
    await api.delete(`/platform/orgs/${o.id}`);
    toast.success("Deleted");
    load();
  };

  const toggleAccess = async (o, field) => {
    try {
      const current = o[field] !== false;
      await api.patch(`/platform/orgs/${o.id}`, { [field]: !current });
      toast.success(
        `${field === "login_enabled" ? "Admin login" : "SIP service"} ${
          !current ? "resumed" : "paused"
        } for ${o.name}`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  // Phase 6o Hotfix G2 — the old `accessMode` helper composed a single
  // "Full access / Login only / SIP only / Paused (both)" pill from the
  // two toggles. The redesigned row surfaces each toggle inline with its
  // own ON/OFF chiclet (clearer at a glance), so the composed pill is
  // no longer needed.

  const filtered = items.filter((o) => {
    const q = filter.toLowerCase();
    return !q || o.name.toLowerCase().includes(q) || o.slug.toLowerCase().includes(q);
  });

  // Phase O+ — 3CX/Vodia-style inline editor takes over the whole page.
  // Shared for both New Tenant and Edit Tenant. The nested
  // <ManageUsersDialog> still uses a modal because it's a secondary
  // flow the operator enters on demand, not a routine form fill.
  if (open) {
    return (
      <InlineEditorShell
        crumb={t("pages.tenants.title")}
        title={editing ? `Edit Tenant${form.name?.trim() ? `: ${form.name.trim()}` : ""}` : "New Tenant"}
        onCancel={() => setOpen(false)}
        onSave={save}
        saveDisabled={!form.name?.trim()}
        testidRoot="tenant-admin-editor"
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="tenant-admin-name"
                required
              />
            </div>
            <div>
              <Label>Slug</Label>
              <Input
                value={form.slug || ""}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="auto from name if empty"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Plan</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.plan}
                onChange={(e) => setForm({ ...form, plan: e.target.value })}
              >
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                <option value="scale">Scale</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <Label>Status</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label>Channels</Label>
              <Input
                type="number"
                value={form.limits.channels}
                onChange={(e) => setForm({ ...form, limits: { ...form.limits, channels: e.target.value } })}
              />
            </div>
            <div>
              <Label>Max trunks</Label>
              <Input
                type="number"
                value={form.limits.max_trunks}
                onChange={(e) => setForm({ ...form, limits: { ...form.limits, max_trunks: e.target.value } })}
              />
            </div>
            <div>
              <Label>Max domains</Label>
              <Input
                type="number"
                value={form.limits.max_domains}
                onChange={(e) => setForm({ ...form, limits: { ...form.limits, max_domains: e.target.value } })}
              />
            </div>
            <div>
              <Label>Max customers</Label>
              <Input
                type="number"
                value={form.limits.max_customers}
                onChange={(e) => setForm({ ...form, limits: { ...form.limits, max_customers: e.target.value } })}
              />
            </div>
          </div>
          {/* Plan flags — per-tenant feature license. Layered on top of
              the platform-level Engine Modules: a feature is effective
              ONLY when both the engine module is ON and the tenant's
              flag is ON. Defaults are off so behaviour is unchanged
              until the operator opts a tenant in. */}
          <div className="rounded-md border border-border p-3 space-y-2 mt-2">
            <div className="text-xs font-medium">Plan features (per tenant)</div>
            <p className="text-[11px] text-muted-foreground">
              Toggle which optional capabilities this tenant is licensed to use. The
              engine still requires the matching Engine Module (Platform → Settings)
              to be ON globally — these are the per-customer overrides.
            </p>
            {[
              {
                key: "failover",
                label: "Failover routing",
                desc: "Allow this tenant's outbound routes to fall back to backup trunks when the primary is unreachable.",
              },
              {
                key: "dialplan",
                label: "Dial-plan transformations",
                desc: "Allow strip-leading / prepend manipulation on this tenant's outbound routes.",
              },
              {
                key: "recording",
                label: "Call recording (coming soon)",
                desc: "Record and store call audio for this tenant. Requires the SBC engine module.",
                comingSoon: true,
              },
              {
                key: "api",
                label: "Programmatic API access",
                desc: "Issue API tokens scoped to this tenant for automation and integrations.",
                comingSoon: true,
              },
            ].map((f) => (
              <div
                key={f.key}
                className="flex items-start justify-between gap-3 rounded px-2 py-1.5 hover:bg-muted/40"
                data-testid={`tenant-feature-${f.key}`}
              >
                <div className="min-w-0">
                  <div className="text-xs font-medium">{f.label}</div>
                  <div className="text-[11px] text-muted-foreground">{f.desc}</div>
                </div>
                <Switch
                  checked={!!form.features?.[f.key] && !f.comingSoon}
                  disabled={!!f.comingSoon}
                  onCheckedChange={(v) =>
                    setForm({
                      ...form,
                      features: { ...(form.features || {}), [f.key]: v },
                    })
                  }
                  data-testid={`tenant-feature-toggle-${f.key}`}
                />
              </div>
            ))}
          </div>
          {/* Phase 6r — Billing & Trial section. Only on edit (the
              backend stamps the trial automatically at create-time
              based on platform_settings.trial_days). The badge mirrors
              the row badge for instant recognition. */}
          {editing && (
            <div className="rounded-md border border-border p-3 space-y-3 mt-2" data-testid="tenant-billing-card">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="text-xs font-medium">{t("pages.tenants.billing.title", "Billing & Trial")}</div>
                  <p className="text-[11px] text-muted-foreground">
                    {t(
                      "pages.tenants.billing.help",
                      "Override the global trial timer or mark this tenant as billing-exempt (beta tester / comp account). Exempt tenants are never billed and their calls are never blocked by trial expiry.",
                    )}
                  </p>
                </div>
                <BillingBadge status={editing.billing_status} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>{t("pages.tenants.billing.trialExpiresAt", "Trial expires at")}</Label>
                  <Input
                    type="date"
                    value={isoToDateInput(form.trial_expires_at)}
                    onChange={(e) =>
                      setForm({ ...form, trial_expires_at: e.target.value || null })
                    }
                    data-testid="tenant-trial-expires-at"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {t(
                      "pages.tenants.billing.trialExpiresHint",
                      "Blank = no trial expiry. Cleared automatically when this tenant moves off the Free plan.",
                    )}
                  </p>
                </div>
                <div>
                  <Label>{t("pages.tenants.billing.exemptUntil", "Billing exempt until")}</Label>
                  <Input
                    type="date"
                    value={isoToDateInput(form.billing_exempt_until)}
                    disabled={!form.billing_exempt}
                    onChange={(e) =>
                      setForm({ ...form, billing_exempt_until: e.target.value || null })
                    }
                    data-testid="tenant-billing-exempt-until"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {t(
                      "pages.tenants.billing.exemptUntilHint",
                      "Blank = exempt forever (until you flip the switch off).",
                    )}
                  </p>
                </div>
              </div>

              <div
                className="flex items-start justify-between gap-3 rounded px-2 py-1.5 hover:bg-muted/40"
                data-testid="tenant-billing-exempt-row"
              >
                <div className="min-w-0">
                  <div className="text-xs font-medium">
                    {t("pages.tenants.billing.exempt", "Billing exempt (beta tester / comp account)")}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {t(
                      "pages.tenants.billing.exemptDesc",
                      "Suppresses billing pipeline (no Stripe sync, no invoicing) and bypasses trial expiry. Toggle off to revert to normal trial / paid behaviour.",
                    )}
                  </div>
                </div>
                <Switch
                  checked={!!form.billing_exempt}
                  onCheckedChange={(v) =>
                    setForm({
                      ...form,
                      billing_exempt: v,
                      // Clear the until-date when toggling exempt off so
                      // we don't carry a stale value if they re-enable.
                      billing_exempt_until: v ? form.billing_exempt_until : null,
                    })
                  }
                  data-testid="tenant-billing-exempt-toggle"
                />
              </div>

              <div>
                <Label>{t("pages.tenants.billing.notes", "Billing notes")}</Label>
                <textarea
                  className="flex min-h-[64px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.billing_notes || ""}
                  maxLength={1000}
                  onChange={(e) => setForm({ ...form, billing_notes: e.target.value })}
                  placeholder={t(
                    "pages.tenants.billing.notesPlaceholder",
                    "Why is this tenant exempt? (e.g. \"Beta program — review monthly\")",
                  )}
                  data-testid="tenant-billing-notes"
                />
              </div>
            </div>
          )}
          {!editing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-border">
              <div>
                <Label>Owner email</Label>
                <Input
                  type="email"
                  value={form.owner_email}
                  onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                  data-testid="tenant-owner-email"
                />
              </div>
              <div>
                <Label>Owner password</Label>
                <PasswordInput
                  value={form.owner_password}
                  onChange={(e) => setForm({ ...form, owner_password: e.target.value })}
                />
              </div>
            </div>
          )}
        </div>
      </InlineEditorShell>
    );
  }

  return (
    <div data-testid="page-platform-tenants">
      <PageHeader
        title={t("pages.tenants.title")}
        subtitle="Each tenant is an ITSP running their own mini-switch on Elswitch."
        icon={Building2}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder={t("common.search") + "…"}
              className="w-full sm:w-[240px]"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <select
              className="flex h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="tenants-status-filter"
            >
              <option value="">{t("common.allStatuses", "All statuses")}</option>
              <option value="pending">{t("common.statusPending", "Pending")}</option>
              <option value="active">{t("common.statusActive", "Active")}</option>
              <option value="suspended">{t("common.statusSuspended", "Suspended")}</option>
            </select>
            <Button
              onClick={() => {
                setEditing(null);
                setForm(EMPTY);
                setOpen(true);
              }}
              data-testid="new-tenant-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> {t("pages.tenants.add")}
            </Button>
          </div>
        }
      />
      {filtered.length === 0 ? (
        <EmptyState
          title={items.length ? t("common.noMatches", "No matches") : t("pages.tenants.noTenants")}
          description={
            items.length
              ? "Try changing the search or status filter."
              : "Create a tenant manually, or enable self-signup in Platform Settings."
          }
        />
      ) : (
        <>
          <p className="text-[11px] text-muted-foreground mb-2 xl:hidden flex items-center gap-1">
            <ChevronRight className="h-3.5 w-3.5" />
            Swipe the table left/right — the <b>Tenant</b> column stays pinned. The <b>Actions</b> column is on the right edge.
          </p>
          <div className="relative rounded-lg border border-border bg-card overflow-x-auto">
            {/* Soft right-edge gradient + chevron — visible on tablet
                widths (anything below xl/1280px) where the table doesn't
                fit fully and Actions risks being cut off. */}
            <div className="pointer-events-none absolute top-0 right-0 h-full w-10 bg-gradient-to-l from-background/85 via-background/40 to-transparent xl:hidden z-10" />
            <div className="pointer-events-none absolute top-1/2 right-1.5 -translate-y-1/2 xl:hidden z-10 rounded-full bg-[hsl(var(--accent))]/15 border border-[hsl(var(--accent))]/40 p-1 animate-pulse">
              <ChevronRight className="h-3.5 w-3.5 text-[hsl(var(--accent))]" />
            </div>
          <Table className="min-w-[760px]">
            <TableHeader className="sticky-table-head">
              <TableRow>
                {/* Phase 6o Hotfix G2 — Customers list compacted from
                    8 cols to 5. LED in the Tenant cell replaces the
                    separate Status column (green=active pulsing,
                    cyan=pending, red=suspended, grey=other). Plan,
                    Limits, Users, Calls all fold into the Tenant +
                    Usage stacked cells. No more sticky-left hack. */}
                <TableHead className="min-w-[260px]">{t("pages.tenants.colTenant", "Tenant")}</TableHead>
                <TableHead className="min-w-[200px]">{t("pages.tenants.colUsage", "Usage")}</TableHead>
                <TableHead className="w-[170px]">{t("pages.tenants.colAccess", "Access")}</TableHead>
                <TableHead className="w-[240px] text-right pr-6">{t("pages.tenants.colActions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((o) => {
                const ledTitleKey = ["active", "pending", "suspended"].includes(o.status)
                  ? o.status
                  : "other";
                const ledTitle = t(`pages.tenants.ledTitle.${ledTitleKey}`);
                const loginOn = o.login_enabled !== false;
                const sipOn = o.sip_enabled !== false;
                const disabled = o.status !== "active";
                const prov = relTimeShort(o.created_at);
                return (
                <TableRow key={o.id} className="row-hover" data-testid={`tenant-row-${o.id}`}>
                  {/* --- Tenant cell: LED + name (top), slug · plan · age (sub) --- */}
                  <TableCell>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <TenantLED status={o.status} title={ledTitle} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">{o.name}</span>
                          {o.status === "pending" && (
                            <StatusPill kind="info">
                              <Clock className="h-3 w-3 mr-0.5" />
                              {t("pages.tenants.ledTitle.pending", "Pending approval")}
                            </StatusPill>
                          )}
                          {o.status === "suspended" && (
                            <StatusPill kind="danger">
                              <ShieldAlert className="h-3 w-3 mr-0.5" />
                              {t("pages.tenants.ledTitle.suspended", "Suspended")}
                            </StatusPill>
                          )}
                          {/* Phase 6r — billing-state badge sits next to
                              the status pills. Only renders when a
                              non-trivial state is reported (paid is
                              implicit — paying customers don't need a
                              chip). */}
                          {o.billing_status && o.billing_status.state !== "paid" && (
                            <BillingBadge status={o.billing_status} />
                          )}
                        </div>
                        <div className="font-mono text-[11px] text-muted-foreground truncate">
                          <span>{o.slug}</span>
                          <span className="mx-1.5 opacity-50">·</span>
                          <span className="uppercase tracking-wider">{o.plan}</span>
                          {prov && (
                            <>
                              <span className="mx-1.5 opacity-50">·</span>
                              <span>{t("pages.tenants.provisioned", { rel: prov })}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  {/* --- Usage cell: live/users (top), limits (sub) --- */}
                  <TableCell>
                    <div className="text-sm tabular-nums">
                      <span data-testid={`tenant-users-${o.id}`}>{o.users_count ?? 0}</span>
                      <span className="text-muted-foreground ml-1">
                        {t("pages.tenants.usage.users")}
                      </span>
                      <span className="mx-2 opacity-50">·</span>
                      <span
                        className={
                          (o.active_calls ?? 0) > 0
                            ? "text-[hsl(var(--accent))] font-semibold"
                            : ""
                        }
                        data-testid={`tenant-live-${o.id}`}
                      >
                        {o.active_calls ?? 0}
                      </span>
                      <span className="text-muted-foreground ml-1">
                        {t("pages.tenants.usage.live")}
                      </span>
                    </div>
                    <div className="font-mono text-[11px] text-muted-foreground mt-0.5">
                      {t("pages.tenants.usage.limits", {
                        ch: o.limits?.channels || 0,
                        tr: o.limits?.max_trunks || 0,
                        cu: o.limits?.max_customers || 0,
                      })}
                    </div>
                  </TableCell>
                  {/* --- Access cell: compact login + sip chiclets --- */}
                  <TableCell>
                    <div
                      className="flex flex-col gap-1.5 items-start"
                      data-testid={`access-cell-${o.id}`}
                    >
                      <label
                        className="flex items-center gap-2 cursor-pointer whitespace-nowrap text-[11px] uppercase tracking-wider text-muted-foreground"
                        title="Toggle admin login"
                      >
                        <Switch
                          checked={loginOn}
                          onCheckedChange={() => toggleAccess(o, "login_enabled")}
                          disabled={disabled}
                          aria-label="Toggle admin login"
                          data-testid={`toggle-login-${o.id}`}
                          className="scale-90"
                        />
                        <KeyRound className="h-3 w-3" />
                        <span className="font-semibold text-foreground/80">
                          {t("pages.tenants.access.login")}
                        </span>
                        <span
                          className={`ml-1 text-[10px] ${
                            loginOn ? "text-[hsl(var(--ok))]" : "text-muted-foreground"
                          }`}
                        >
                          {loginOn ? "ON" : "OFF"}
                        </span>
                      </label>
                      <label
                        className="flex items-center gap-2 cursor-pointer whitespace-nowrap text-[11px] uppercase tracking-wider text-muted-foreground"
                        title="Toggle SIP service"
                      >
                        <Switch
                          checked={sipOn}
                          onCheckedChange={() => toggleAccess(o, "sip_enabled")}
                          disabled={disabled}
                          aria-label="Toggle SIP service"
                          data-testid={`toggle-sip-${o.id}`}
                          className="scale-90"
                        />
                        <PhoneCall className="h-3 w-3" />
                        <span className="font-semibold text-foreground/80">
                          {t("pages.tenants.access.sip")}
                        </span>
                        <span
                          className={`ml-1 text-[10px] ${
                            sipOn ? "text-[hsl(var(--ok))]" : "text-muted-foreground"
                          }`}
                        >
                          {sipOn ? "ON" : "OFF"}
                        </span>
                      </label>
                    </div>
                  </TableCell>
                  {/* --- Actions cell: lifecycle + manage + edit + delete --- */}
                  <TableCell className="text-right whitespace-nowrap space-x-1">
                    {o.status === "pending" && (
                      <Button size="sm" onClick={() => approve(o)} data-testid={`approve-${o.id}`}>
                        Approve
                      </Button>
                    )}
                    {o.status === "active" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => suspend(o)}
                        data-testid={`suspend-${o.id}`}
                      >
                        Suspend
                      </Button>
                    )}
                    {o.status === "suspended" && (
                      <Button size="sm" onClick={() => reactivate(o)} data-testid={`reactivate-${o.id}`}>
                        Reactivate
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setUsersDialogOrg(o)}
                      data-testid={`manage-users-${o.id}`}
                      title="Manage users (reset password, enable/disable)"
                    >
                      <Users className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => {
                        setEditing(o);
                        setForm({ ...EMPTY, ...o });
                        setOpen(true);
                      }}
                      data-testid={`edit-tenant-${o.id}`}
                      title="Edit tenant"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-[hsl(var(--danger))]"
                      onClick={() => del(o)}
                      data-testid={`delete-tenant-${o.id}`}
                      title="Delete tenant"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );})}
            </TableBody>
          </Table>
          </div>
        </>
      )}

      <ManageUsersDialog
        org={usersDialogOrg}
        open={!!usersDialogOrg}
        onOpenChange={(v) => !v && setUsersDialogOrg(null)}
      />
    </div>
  );
}
